import React, { memo } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useDoctorShiftRuleList } from '../hook/useDoctorShiftRuleList';
import type { DoctorShiftRuleRepository } from '../../../../application/doctorShiftRule';
import DoctorShiftRuleTable from '../components/DoctorShiftRuleTable';
import DoctorShiftRuleAddModal from '../components/DoctorShiftRuleAddModal';
import DoctorShiftRuleEditModal from '../components/DoctorShiftRuleEditModal';
import DoctorShiftRuleDeleteDialog from '../components/DoctorShiftRuleDeleteDialog';
import {
  HeaderSection,
  TitleArea,
  ActionArea,
  FilterSection,
  TableSection,
  ErrorContainer
} from '../DoctorShiftRuleList.styles';

interface DoctorShiftRuleListContainerProps {
  repository: DoctorShiftRuleRepository;
}

/**
 * 医師シフトルール一覧コンテナ
 *
 * - useDoctorShiftRuleList Hook使用
 * - コンポーネント組み立て
 * - Repository注入
 */
const DoctorShiftRuleListContainer: React.FC<DoctorShiftRuleListContainerProps> = memo(({
  repository
}) => {
  const {
    data,
    meta,
    isLoading,
    error,
    addModalOpen,
    editModalOpen,
    deleteDialogOpen,
    selectedRule,
    handlePageChange,
    handleSearch,
    refresh,
    showAddModal,
    showEditModal,
    showDeleteDialog,
    closeModals,
    createRule,
    updateRule,
    deleteRule,
    clearError
  } = useDoctorShiftRuleList(repository);

  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearchSubmit = () => {
    handleSearch(searchTerm);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  return (
    <Box>
      {/* ヘッダー */}
      <HeaderSection>
        <TitleArea>
          {/* タイトルは親コンポーネントで設定 */}
        </TitleArea>
        <ActionArea>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={isLoading}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={showAddModal}
          >
            ルールを追加
          </Button>
        </ActionArea>
      </HeaderSection>

      {/* エラー表示 */}
      {error && (
        <ErrorContainer>
          <Alert
            severity="error"
            onClose={clearError}
          >
            {error}
          </Alert>
        </ErrorContainer>
      )}

      {/* 検索フィルター */}
      <FilterSection>
        <TextField
          size="small"
          placeholder="医師名または医師コードで検索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="outlined"
          onClick={handleSearchSubmit}
          disabled={isLoading}
        >
          検索
        </Button>
      </FilterSection>

      {/* テーブル */}
      <TableSection>
        <DoctorShiftRuleTable
          data={data}
          isLoading={isLoading}
          meta={meta}
          onPageChange={handlePageChange}
          onEdit={showEditModal}
          onDelete={showDeleteDialog}
        />
      </TableSection>

      {/* 追加モーダル */}
      <DoctorShiftRuleAddModal
        open={addModalOpen}
        onClose={closeModals}
        onSubmit={createRule}
        isLoading={isLoading}
        error={error}
      />

      {/* 編集モーダル */}
      <DoctorShiftRuleEditModal
        open={editModalOpen}
        rule={selectedRule}
        onClose={closeModals}
        onSubmit={updateRule}
        isLoading={isLoading}
        error={error}
      />

      {/* 削除確認ダイアログ */}
      <DoctorShiftRuleDeleteDialog
        open={deleteDialogOpen}
        rule={selectedRule}
        onClose={closeModals}
        onConfirm={deleteRule}
        isLoading={isLoading}
      />
    </Box>
  );
});

DoctorShiftRuleListContainer.displayName = 'DoctorShiftRuleListContainer';

export default DoctorShiftRuleListContainer;
